import { CheckIcon, WarningTwoIcon } from '@chakra-ui/icons';
import { Box, Flex, Spacer, useMultiStyleConfig, StyleProps, CSSObject } from '@chakra-ui/react';
import { kebabCase, merge } from 'lodash';
import React, { ComponentType } from 'react';
import ReactSelect, {
  components,
  GroupTypeBase,
  ValueContainerProps,
  Props,
  OptionTypeBase,
  OptionProps,
  NamedProps,
} from 'react-select';

const pseudoStatesToClassMap = {
  _focus: 'is-focused',
  _selected: 'is-selected',
  _disabled: 'is-disabled',
};

const multipartThemeToClassNames: (themeStyles: StyleProps) => CSSObject = (themeStyles, prefix = '.react-select__') => {
  return Object.fromEntries(
    Object.entries(themeStyles).flatMap(([part, styles]) => {
      // Calculate the base class for this part
      // For react-select, all of these are the component keys in the styles object in kebab case (e.g. dropdownIndicator --> dropdown-indicator) - https://react-select.com/styles#style-object
      const baseClass = `${prefix}${kebabCase(part)}`;
      const baseStyle = [baseClass, styles];
      // For pseudo states (e.g. focused, selected, disabled) react-select implements predictable classes (`{baseClass}--{pseudoClass}`)
      // Here we calculate the required classes & styles to apply those styles if they are present
      const pseudoStates = Object.entries(styles)
        .filter(([key]) => Object.keys(pseudoStatesToClassMap).includes(key))
        .map(([key, pseduoStyles]) => {
          const className = `${baseClass}--${pseudoStatesToClassMap[key]}`;
          return [className, pseduoStyles];
        });
      return [baseStyle, ...pseudoStates];
    }),
  );
};

// reset the react-select theme
const resetTheme = (theme) => ({
  ...theme,
  spacing: {
    baseUnit: 0,
    controlHeight: 32,
  },
});

export interface OptionType extends OptionTypeBase {
  value: string;
  label: string;
  color?: string;
}

// ValueContainer with a WarningIcon when the component isInvalid = true
export const ValueContainer: ComponentType<ValueContainerProps<OptionType, false, GroupTypeBase<OptionType>>> = ({
  children,
  ...props
}) => {
  const {
    selectProps: { isInvalid, value },
  } = props;
  const color = (value as OptionType)?.color;

  return (
    <components.ValueContainer {...props}>
      <Box bgColor={color} w="100%">
        {children}
      </Box>
      {isInvalid ? (
        <Box ml="auto" pr={3}>
          <WarningTwoIcon color="error" />
        </Box>
      ) : null}
    </components.ValueContainer>
  );
};

export const OptionContainer = ({ children, ...props }: OptionProps<OptionTypeBase, false>) => {
  return (
    <components.Option {...props}>
      <Box bgColor={props?.data?.color} p={2} margin={0}>
        {children}
      </Box>
    </components.Option>
  );
};

/**
 * Option component that contains a CheckIcon. Use this
 * component to wrap the option details when displaying a selected option.
 */
export const SelectedOption = ({ children }) => (
  <Flex direction="row" alignItems="center">
    {children}
    <Spacer />
    <Box>
      <CheckIcon fontSize="sm" color="secondary.base" />
    </Box>
  </Flex>
);

export type SelectProps = Props<{
  value: any;
  label: string;
  color?: string;
}> & {
  isInvalid?: boolean;
  width?: string | number;
  menuMinWidth?: string | number;
  placeholder?: string;
  styles?: StyleProps; // deprecated
  styleOverrides?: Record<string, StyleProps>;
};

export const Select = ({ styles: deprecatedStyleOverrides, styleOverrides, placeholder, ...props }: SelectProps) => {
  const styles = useMultiStyleConfig('ReactSelect', props);
  return (
    <Box __css={multipartThemeToClassNames(merge(styles, deprecatedStyleOverrides, styleOverrides))}>
      <ReactSelect
        {...props}
        placeholder={placeholder}
        theme={resetTheme}
        classNamePrefix="react-select"
        className="react-select__container"
      />
    </Box>
  );
};
